import React, { useCallback, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Progress } from 'src/components/ui/progress';
import { Button } from 'src/components/ui/button/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useToast } from 'src/components/ui/use-toast';
import { authQueries } from 'src/api/queries';
import {
  RequestStatusBadge,
  StatusBadgeWithLoader,
} from 'src/features/requests/request-status-badge';
import { Logger } from 'src/utils/logger';
import { dismissMessages, formatUserFacingError, uploadRetryMessages } from './messages';
import {
  isUserRetryAllowed,
  useBackgroundUploadStore,
} from './use-background-upload-store';
import { useRetryFailedUpload } from './use-retry-failed-upload';
import {
  readPersistedUploads,
  removePersistedUploadStorage,
} from './persisted-uploads-storage';

interface UploadingStatusCellProps {
  requestId: string;
  size?: 'sm' | 'default';
}

const UploadingStatusCell: React.FC<UploadingStatusCellProps> = ({ requestId, size = 'sm' }) => {
  const progress = useBackgroundUploadStore((s) => s.getOverallProgress(requestId));
  const request = useBackgroundUploadStore((s) => s.activeRequests[requestId]);
  const { retry } = useRetryFailedUpload();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRetrying, setIsRetrying] = useState(false);

  const failedUploads = request?.pendingUploads.filter((u) => u.status === 'failed') ?? [];
  const hasFailed = failedUploads.length > 0;
  const completedCount = request?.pendingUploads.filter((u) => u.status === 'attached').length ?? 0;
  const totalCount = request?.pendingUploads.length ?? 0;
  // Curated tooltip copy across *all* failed uploads. Distinct curated messages
  // are enumerated so a request whose files fail with different root causes
  // (e.g. one tus failure + one attach failure) doesn't silently surface only
  // the first. Raw tus / API error strings still return null and are hidden so
  // the user never sees internal jargon like "Failed to attach media".
  // (Computed inline — failedUploads is rebuilt every render so memoization
  // wouldn't actually cache across renders.)
  const distinctTooltipErrors = Array.from(
    new Set(
      failedUploads
        .map((u) => formatUserFacingError(u.error))
        .filter((m): m is string => m !== null),
    ),
  );
  // Disable Retry once *every* failed upload in this request has hit the
  // user-retry cap. Mixed cap state (some allowed, some not) still allows
  // Retry — handleRetry skips capped ones individually.
  const anyRetryAllowed = failedUploads.some(isUserRetryAllowed);

  // Mirror isRetrying into a ref so handleRetry's useCallback stays stable
  // across the start/end of an in-flight retry. The ref doubles as a
  // synchronous re-entry guard: setIsRetrying only takes effect after React
  // commits the render, so back-to-back clicks within the same event tick
  // would otherwise both pass an isRetrying-state guard. We flip the ref
  // synchronously inside the handler to close that window.
  const isRetryingRef = useRef(isRetrying);
  isRetryingRef.current = isRetrying;

  const handleRetry = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const currentRequest = useBackgroundUploadStore.getState().activeRequests[requestId];
      if (!currentRequest || isRetryingRef.current) return;
      const currentFailedUploads = currentRequest.pendingUploads.filter(
        (u) => u.status === 'failed',
      );
      if (!currentFailedUploads.some(isUserRetryAllowed)) {
        toast({
          variant: 'destructive',
          description: uploadRetryMessages.retryLimitReached,
        });
        return;
      }
      // Flip the ref *before* setIsRetrying so a second click on the same
      // event tick (before React commits the disabled re-render) is blocked
      // by the ref guard above.
      isRetryingRef.current = true;
      setIsRetrying(true);
      try {
        // Snapshot to avoid racing store updates while we iterate.
        const toRetry = [...currentFailedUploads];
        let cappedCount = 0;
        for (const pending of toRetry) {
          // Re-read the upload's current status from the store. The orchestrator's
          // 2s poll (or another handler) may have flipped this pending upload out
          // of 'failed' between snapshot time and this iteration — retrying a
          // non-failed upload would double-trigger attach work.
          const current = useBackgroundUploadStore
            .getState()
            .activeRequests[requestId]?.pendingUploads.find(
              (u) => u.tempId === pending.tempId,
            );
          if (!current || current.status !== 'failed') continue;
          if (!isUserRetryAllowed(current)) {
            cappedCount += 1;
            continue;
          }
          await retry(requestId, pending);
        }
        if (cappedCount > 0) {
          toast({
            variant: 'destructive',
            description: uploadRetryMessages.retryLimitReached,
          });
        }
      } finally {
        isRetryingRef.current = false;
        setIsRetrying(false);
      }
    },
    [requestId, retry, toast],
  );

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Free the IndexedDB file + localStorage metadata for each non-attached
    // upload before we drop our local tracking. Without this, dismissed
    // failed uploads accumulate GBs of video data in IndexedDB over time.
    //
    // Read persisted uploads directly from localStorage rather than via
    // useMediaUpload(): this cell is rendered from the dashboard and the
    // requests-list, neither of which sits inside MediaUploadProvider, so
    // the hook would throw. localStorage is the same source the provider
    // mirrors anyway. Match persisted entries by id (= pendingUpload.tempId,
    // both are the server-issued upload id) so two requests with same-name
    // same-size files don't cross-delete each other's IDB blobs.
    if (request) {
      const persistedList = readPersistedUploads();
      for (const pending of request.pendingUploads) {
        if (pending.status === 'attached') continue;
        const persisted = persistedList.find((p) => p.id === pending.tempId);
        if (persisted) {
          removePersistedUploadStorage(persisted.id).catch((err) => {
            // Cleanup best-effort; the rehydrate TTL will eventually evict.
            // Logged so repeated failures show up in Sentry/Telescope instead
            // of silently leaving GBs of video in IndexedDB.
            Logger.warn(
              'Failed to remove persisted upload from IndexedDB on dismiss',
              'UploadingStatusCell',
              { requestId, persistedId: persisted.id, fileName: pending.fileName, err },
            );
          });
        }
      }
    }

    useBackgroundUploadStore.getState().removeRequest(requestId);
    queryClient.invalidateQueries({ queryKey: [authQueries.requests, 'uploading'] });
    queryClient.invalidateQueries({ queryKey: [authQueries.requests, 'all'] });
    queryClient.invalidateQueries({ queryKey: [authQueries.requestCounts] });
    toast({
      description: dismissMessages.removed,
    });
  };

  const retryLabel = isRetrying ? 'Retrying' : 'Retry';
  const retryDisabled = isRetrying || !anyRetryAllowed;
  const retryButtonAriaLabel = anyRetryAllowed
    ? `${retryLabel} failed upload`
    : 'Retry limit reached';
  const retryTooltipCopy = anyRetryAllowed
    ? `${retryLabel} upload`
    : 'Retry limit reached — re-submit the request';

  return (
    <div className="tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-overflow-hidden">
      {hasFailed ? (
        <TooltipProvider>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <RequestStatusBadge size={size} variant={'upload_failed'} />
                </span>
              </TooltipTrigger>
              {distinctTooltipErrors.length > 0 && (
                <TooltipContent side="top" align="center">
                  <div className="tw-flex tw-flex-col tw-gap-0.5">
                    {distinctTooltipErrors.map((m) => (
                      <span key={m} className="tw-text-xs">
                        {m}
                      </span>
                    ))}
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
            <Tooltip>
              {/*
               * Wrap the Button in a span so the tooltip keeps firing when the
               * button is disabled. Button's `disabled:tw-pointer-events-none`
               * variant otherwise swallows hover events on the trigger, hiding
               * the "Retry limit reached — re-submit the request" copy that's
               * the whole reason the button is disabled.
               */}
              <TooltipTrigger asChild>
                <span className="tw-inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={handleRetry}
                    disabled={retryDisabled}
                    className="tw-gap-1 tw-text-xs"
                    aria-label={retryButtonAriaLabel}
                  >
                    <RefreshCw
                      className={`tw-h-3 tw-w-3 ${isRetrying ? 'tw-animate-spin' : ''}`}
                    />
                    <span className="tw-hidden md:tw-inline">{retryLabel}</span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <span className="tw-text-xs">{retryTooltipCopy}</span>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconXs"
                  onClick={handleDismiss}
                  aria-label="Dismiss failed upload and remove from this device"
                >
                  <X className="tw-h-3 tw-w-3" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <span className="tw-text-xs">Remove upload</span>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ) : (
        // Loader sits INSIDE the pill (not next to it) per #2372 follow-up
        // so the upload-in-flight signal is consistent with the re-edit
        // cycle loader added in #2383.
        <StatusBadgeWithLoader size={size} variant={'uploading'} />
      )}
      {request && (
        <>
          <Progress value={progress} className="tw-h-1.5 tw-w-full" />
          <span className="tw-truncate tw-text-xs tw-text-muted-foreground">
            {completedCount}/{totalCount} files
            {hasFailed && ` (${failedUploads.length} failed)`}
          </span>
        </>
      )}
    </div>
  );
};

export { UploadingStatusCell };
